import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// react
import { useCallback, useMemo, useState } from 'react';
// components
import { Avatar, Button, Dialog } from "../../atoms";
import { ProfilePreview } from "../../molecules";
import { InviteMembersByEmailForm, InviteMembersForm, } from "../../organisms";
import { TablePage } from "..";
// tanstack
import { createColumnHelper } from '@tanstack/react-table';
// columns
const columnHelper = createColumnHelper();
/**
 * InvitePortal shows the accounts and invited members of an organization
 */
export const InvitePortal = ({ organization, accounts, inviteMembers, addInviteKeys, notifyToast, resendInvite, resendEmailInvites, isBackButton = true, isAdmin, }) => {
    var _a;
    // state
    const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [isShowInviteMembers, setIsShowInviteMembers] = useState(false);
    const [isShowAddInviteKeys, setIsShowAddInviteKeys] = useState(false);
    // memo
    const columns = useMemo(() => [
        columnHelper.accessor('imgSrc', {
            header: '',
            cell: (info) => {
                var _a, _b, _c, _d;
                const row = info.cell.row.original;
                let fallback = `${((_a = row.firstName) === null || _a === void 0 ? void 0 : _a[0]) || ''}${((_b = row.lastName) === null || _b === void 0 ? void 0 : _b[0]) || ''}`.toUpperCase();
                if (fallback.length === 0) {
                    if (row.email && row.email.length > 0) {
                        fallback = row.email[0].toUpperCase();
                    }
                    else {
                        ((_d = (_c = row.inviteKey) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.toUpperCase()) || '';
                    }
                }
                return _jsx(Avatar, { size: 'xs', fallback: fallback });
            },
        }),
        columnHelper.accessor('fullName', {
            header: 'Name',
        }),
        columnHelper.accessor('email', {
            header: 'Email',
        }),
        ...((organization === null || organization === void 0 ? void 0 : organization.isInviteKeyEnabled)
            ? [
                columnHelper.accessor('inviteKey', {
                    header: organization.inviteKeyName || 'Payroll ID',
                }),
            ]
            : []),
        columnHelper.accessor('isRegistered', {
            header: 'Registered',
            cell: (info) => {
                return info.getValue() ? 'Yes' : 'No';
            },
        }),
        columnHelper.accessor('accountRole.name', {
            header: 'Account Role',
            cell: (info) => {
                return info.getValue() || 'Member';
            },
        }),
    ], [organization]);
    const selectedProfileInitials = useMemo(() => {
        var _a, _b, _c, _d;
        if (!selectedProfile)
            return '';
        return ((((_b = (_a = selectedProfile === null || selectedProfile === void 0 ? void 0 : selectedProfile.firstName) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.toUpperCase()) || '') +
            (((_d = (_c = selectedProfile === null || selectedProfile === void 0 ? void 0 : selectedProfile.lastName) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.toUpperCase()) || ''));
    }, [selectedProfile]);
    const actions = useMemo(() => {
        return [
            {
                label: 'Reinvite',
                isHidden: (account) => { var _a; return !!account.isRegistered || (((_a = account.email) === null || _a === void 0 ? void 0 : _a.length) || 0) === 0; },
                onPress: (account) => {
                    if (account) {
                        resendInvite(account.id);
                        notifyToast({
                            title: 'Invitation Success',
                            description: `Resent invitation to ${account.email || 'the user'}`,
                        });
                    }
                },
            },
            {
                label: 'View',
                isHidden: (account) => !account.isRegistered,
                onPress: (account) => {
                    const accountAsAccount = Object.assign(Object.assign({}, account), { email: account.email || '' });
                    setSelectedProfile(accountAsAccount);
                    setIsProfilePreviewOpen(true);
                },
            },
        ];
    }, [inviteMembers, notifyToast]);
    // callback
    const submitMembers = useCallback((req) => {
        const { inviteKeys } = req;
        inviteMembers({ emails: inviteKeys });
        setIsShowInviteMembers(false);
        notifyToast({
            title: 'Invitation Success',
            description: `Sent invitations to ${inviteKeys.slice(0, 3).join(', ')}${inviteKeys.length > 3 ? `, and ${inviteKeys.length - 3} more` : ''}`,
        });
    }, [inviteMembers, notifyToast]);
    const submitInviteKeys = useCallback((req) => {
        const { inviteKeys } = req;
        addInviteKeys(req);
        setIsShowAddInviteKeys(false);
        notifyToast({
            title: 'Invitation Success',
            description: `Added ${organization === null || organization === void 0 ? void 0 : organization.inviteKeyName}s for ${inviteKeys
                .slice(0, 3)
                .join(', ')}${inviteKeys.length > 3 ? `, and ${inviteKeys.length - 3} more` : ''}`,
        });
    }, [addInviteKeys, notifyToast]);
    return (_jsxs(_Fragment, { children: [_jsx(TablePage, { title: `${organization === null || organization === void 0 ? void 0 : organization.name} Invite Portal`, columns: columns, rows: accounts, actions: actions, isBackButton: isBackButton, toolbarActions: _jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ ariaLabel: 'invite new members', size: 'sm', onPress: () => setIsShowInviteMembers(true), iconLeft: 'plus', isRectangle: true }, { children: "Invite New Members" })), (organization === null || organization === void 0 ? void 0 : organization.isInviteKeyEnabled) ? (_jsxs(Button, Object.assign({ ariaLabel: 'upload invite keys', size: 'sm', onPress: () => setIsShowAddInviteKeys(true), iconLeft: 'plus', isRectangle: true }, { children: ["Upload ", organization === null || organization === void 0 ? void 0 : organization.inviteKeyName, "s"] }))) : null, isAdmin && (_jsx(Button, Object.assign({ iconLeft: 'paper-plane-top', ariaLabel: 'Resend All Pending Invites', size: 'sm', isRectangle: true, onPress: resendEmailInvites }, { children: "Resend All Pending Invites" })))] }) }), _jsx(Dialog, Object.assign({ title: 'Invite Members', isCloseButton: true, isOpen: isShowInviteMembers, setIsOpen: setIsShowInviteMembers }, { children: _jsx(InviteMembersByEmailForm, { onSubmit: submitMembers, organization: organization }) })), _jsx(Dialog, Object.assign({ title: `Upload ${organization === null || organization === void 0 ? void 0 : organization.inviteKeyName}s`, isCloseButton: true, isOpen: isShowAddInviteKeys, setIsOpen: setIsShowAddInviteKeys }, { children: _jsx(InviteMembersForm, { onSubmit: submitInviteKeys, organization: organization, isValid: () => true, inputType: 'text', inviteKeyName: (organization === null || organization === void 0 ? void 0 : organization.inviteKeyName) || '' }) })), _jsx(Dialog, Object.assign({ variant: 'sheet', isOpen: isProfilePreviewOpen, setIsOpen: setIsProfilePreviewOpen }, { children: _jsx(ProfilePreview, { fallback: selectedProfileInitials, name: `${(selectedProfile === null || selectedProfile === void 0 ? void 0 : selectedProfile.firstName) || ''} ${(selectedProfile === null || selectedProfile === void 0 ? void 0 : selectedProfile.lastName) || ''}`, email: selectedProfile === null || selectedProfile === void 0 ? void 0 : selectedProfile.email, role: (_a = selectedProfile === null || selectedProfile === void 0 ? void 0 : selectedProfile.accountRole) === null || _a === void 0 ? void 0 : _a.name, onClose: () => setIsProfilePreviewOpen(false), isActionsDisabled: true }) }))] }));
};
InvitePortal.displayName = 'InvitePortal';
export default InvitePortal;
